import { useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useProcInstanceContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useSubmitProcInstance } from '@/shared/api';
import { UserInstanceParams } from '@/shared/types';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const { Title, Paragraph } = Typography;

export const ProcSubmitButton = () => {
  const { code } = useParams<UserInstanceParams>();
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { procInstance } = useProcInstanceContext();

  const [modalOpen, setModalOpen] = useState(false);

  const { mutate: submitInstance, isLoading: submitInstanceLoading } =
    useSubmitProcInstance();

  const handleSubmit = () => {
    try {
      submitInstance(
        { pathParams: { id: procInstance?.id || '' }, queryParams: { code } },
        {
          onSuccess() {
            queryClient.invalidateQueries({
              queryKey: ['instances', procInstance?.id],
            });
            setModalOpen(false);
            showNotification({
              type: 'success',
              message: 'Proc submitted',
              description: 'Your proc is successfully submitted.',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'proc_instance',
        action: 'submit_instance',
        metadata: {
          instanceId: procInstance?.id,
          stepId: 'unknown',
        },
      });
    }
  };

  return (
    <>
      <Button
        type="primary"
        className="instance-float-button"
        icon={<CheckOutlined />}
        disabled={procInstance?.state === 'deprecated'}
        onClick={() => setModalOpen(!modalOpen)}
      >
        Submit
      </Button>
      <Modal
        title={
          <Col
            alignItems="flex-start"
            style={{ margin: '10px 0', width: '100%' }}
          >
            <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
              Submit proc?
            </Title>
          </Col>
        }
        destroyOnClose
        width={450}
        open={modalOpen}
        footer={null}
        style={{ maxWidth: '90%' }}
        onCancel={() => setModalOpen(false)}
      >
        <Paragraph style={{ marginBottom: 20 }}>
          By submitting your proc author will be notified about completion.{' '}
          <strong>
            You will not be able to edit your proc after submission but comment
            section will still be enabled.
          </strong>
        </Paragraph>
        <Row justifyContent="flex-end" gap={15} style={{ marginTop: 30 }}>
          <Button size="large" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={submitInstanceLoading}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Row>
      </Modal>
    </>
  );
};
